import {
  acceptMeetingRequest,
  addEventUser,
  createEvent,
  deleteEvent,
  fetchEventUsers,
  getEvents,
  joinEvent,
  listMeetingRequests,
  rejectMeetingRequest,
  removeEventCover,
  removeEventUser,
  replanMeetingRequest,
  sendMeetingRequest,
  setUserCard,
  setUserType,
  showEvent,
  updateEvent,
  uploadEventCover,
} from '@/services/eventService';
import {
  CreateEventForm,
  Event,
  EventUser,
  MeetingRequest,
} from '@/types/event';
import { immer } from 'zustand/middleware/immer';

import { Query } from '@/types/query';
import { PaginatedResponse, Response } from '@/types/response';
import { handleError } from '@/utils/handleError';
import toast from 'react-hot-toast';
import { create } from 'zustand';

export type EventQuery = Query & { type?: string };
interface EventState {
  events: Event[];
  event: Event | null;
  users: EventUser[];
  loading: boolean;
  filters: EventQuery;
  meetingRequests: MeetingRequest[];
  resetUsers: () => void;
  fetchEvents: (query: Query) => Promise<void>;
  setFilters: (filters: Partial<EventQuery>) => void;
  createEvent: (event: CreateEventForm) => Promise<Response<Event>>;
  showEvent: (eventId: number) => Promise<void>;
  updateEvent: (eventId: number, event: Partial<Event>) => Promise<void>;
  deleteEvent: (eventId: number) => Promise<void>;
  uploadEventCover: (eventId: number, file: string) => Promise<void>;
  removeEventCover: (eventId: number) => Promise<void>;
  fetchEventUsers: (eventId: number, query: EventQuery) => Promise<void>;
  addEventUser: (eventId: number, userId: number) => Promise<void>;
  removeEventUser: (eventId: number, userId: number) => Promise<void>;
  setUserCard: (
    eventId: number,
    userId: number,
    cardSlug: string | null,
  ) => Promise<void>;
  joinEvent: (eventCode: string) => Promise<Response<Event>>;
  sendMeetingRequest: (
    eventId: number,
    userId: number,
    date: string,
    message: string,
  ) => Promise<void>;
  listMeetingRequests: (eventId: number) => Promise<void>;
  acceptMeetingRequest: (meetingRequestId: number) => Promise<void>;
  rejectMeetingRequest: (meetingRequestId: number) => Promise<void>;
  replanMeetingRequest: (
    meetingRequestId: number,
    newDate: string,
  ) => Promise<void>;
  setUserType: (
    eventId: number,
    userId: number,
    type: 'exhibitor' | 'visitor' | 'expert',
  ) => Promise<void>;
}

const initialFilters: EventQuery = {
  current_page: 1,
  per_page: 10,
  search: '',
  sort_field: 'id',
  sort_order: 'desc',
  type: 'all',
  user_id: undefined,
};

export const useEventStore = create<EventState>()(
  immer<EventState>(set => ({
    events: [],
    event: null,
    users: [],
    loading: false,
    filters: initialFilters,
    meetingRequests: [],

    resetUsers: () => {
      set(state => {
        state.users = [];
      });
    },

    fetchEvents: async (query: Query) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await getEvents(query);

        if ('data' in response && 'pagination' in response.data) {
          const paginatedResponse = response as PaginatedResponse<Event>;
          set(state => {
            state.events =
              query.current_page === 1
                ? paginatedResponse.data.data
                : [...state.events, ...paginatedResponse.data.data];

            state.filters = {
              ...state.filters,
              per_page: paginatedResponse.data.pagination.per_page,
              current_page: paginatedResponse.data.pagination.current_page,
              total: paginatedResponse.data.pagination.total,
              last_page: paginatedResponse.data.pagination.last_page,
            };
          });
        } else {
          const simpleResponse = response as Response<Event[]>;
          set({
            events: simpleResponse.data,
            filters: {
              ...query,
              total: simpleResponse.data.length,
              last_page: 1,
            },
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    setFilters: (newFilters: Partial<EventQuery>) => {
      set(state => ({
        filters: { ...state.filters, ...newFilters },
      }));
    },

    createEvent: async (event: CreateEventForm) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await createEvent(event);
        set(state => ({
          events: [...state.events, response.data],
        }));
        return response;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    showEvent: async (eventId: number) => {
      try {
        set(state => {
          state.loading = true;
          state.event = null;
        });
        const { data } = await showEvent(eventId);
        set({ event: data });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    updateEvent: async (eventId: number, event: Partial<Event>) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data, message } = await updateEvent(eventId, event);
        set(state => ({ event: { ...state.event, ...data } }));
        toast.success(message);
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    deleteEvent: async (eventId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        await deleteEvent(eventId);
        set(state => ({
          events: state.events.filter(event => event.id !== eventId),
        }));
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    uploadEventCover: async (eventId: number, file: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await uploadEventCover(eventId, file);
        set(state => ({ event: { ...state.event, ...data } }));
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    removeEventCover: async (eventId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        await removeEventCover(eventId);
        set(state => ({
          ...state,
          event: state.event ? { ...state.event, cover: null } : null,
        }));
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    fetchEventUsers: async (eventId: number, query: EventQuery) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await fetchEventUsers(eventId, query);

        if ('data' in response && 'pagination' in response.data) {
          const paginatedResponse = response as PaginatedResponse<EventUser>;

          set(state => {
            state.users =
              query.current_page === 1
                ? paginatedResponse.data.data
                : [...state.users, ...paginatedResponse.data.data];

            state.filters = {
              ...state.filters,
              per_page: paginatedResponse.data.pagination.per_page,
              current_page: paginatedResponse.data.pagination.current_page,
              last_page: paginatedResponse.data.pagination.last_page,
              total: paginatedResponse.data.pagination.total,
            };
          });
        } else {
          const simpleResponse = response as Response<EventUser[]>;
          set(state => ({
            users: simpleResponse.data,
            filters: {
              ...state.filters,
              ...query,
              total: simpleResponse.data.length,
              last_page: 1,
              current_page: 1,
            },
          }));
        }
      } catch (error) {
        handleError(error);
      }
      set(state => {
        state.loading = false;
      });
    },

    addEventUser: async (eventId: number, userId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { data } = await addEventUser(eventId, userId);
        set(state => ({
          users: [...state.users, data],
          event: state.event
            ? {
                ...state.event,
                user_is_subscribed: true,
              }
            : null,
        }));
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    removeEventUser: async (eventId: number, userId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        await removeEventUser(eventId, userId);
        set(state => ({
          users: state.users.filter(user => user.id !== userId),
          event: state.event
            ? {
                ...state.event,
                user_is_subscribed: false,
                user_card_slug: null,
              }
            : null,
        }));
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    setUserCard: async (
      eventId: number,
      userId: number,
      cardSlug: string | null,
    ) => {
      set(state => {
        state.loading = true;
      });
      try {
        const response = await setUserCard(eventId, userId, cardSlug);
        if (response.data) {
          set({ event: response.data });
        }
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    joinEvent: async (eventCode: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await joinEvent(eventCode);
        set(state => ({
          events: [...state.events, response.data],
        }));
        return response;
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    sendMeetingRequest: async (
      eventId: number,
      userId: number,
      date: string,
      message: string,
    ) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await sendMeetingRequest(
          eventId,
          userId,
          date,
          message,
        );
        toast.success(response.message);
        set(state => ({
          meetingRequests: [...state.meetingRequests, response.data],
        }));
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    listMeetingRequests: async (eventId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await listMeetingRequests(eventId);
        set({
          meetingRequests: response.data,
        });
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    acceptMeetingRequest: async (meetingRequestId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await acceptMeetingRequest(meetingRequestId);
        set(state => ({
          meetingRequests: state.meetingRequests.map(request =>
            request.id === meetingRequestId ? response.data : request,
          ),
        }));
        toast.success(response.message);
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    rejectMeetingRequest: async (meetingRequestId: number) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await rejectMeetingRequest(meetingRequestId);
        set(state => ({
          meetingRequests: state.meetingRequests.map(request =>
            request.id === meetingRequestId ? response.data : request,
          ),
        }));
        toast.success(response.message);
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    replanMeetingRequest: async (meetingRequestId: number, newDate: string) => {
      try {
        set(state => {
          state.loading = true;
        });
        const response = await replanMeetingRequest(meetingRequestId, newDate);
        set(state => ({
          meetingRequests: state.meetingRequests.map(request =>
            request.id === meetingRequestId ? response.data : request,
          ),
        }));
        toast.success(response.message);
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },

    setUserType: async (
      eventId: number,
      userId: number,
      type: 'exhibitor' | 'visitor' | 'expert',
    ) => {
      try {
        set(state => {
          state.loading = true;
        });
        const { message, data } = await setUserType(eventId, userId, type);
        set(state => ({
          users: state.users.map(user =>
            user.id === userId ? { ...user, type: data.type } : user,
          ),
        }));
        toast.success(message);
      } catch (error) {
        handleError(error);
      } finally {
        set(state => {
          state.loading = false;
        });
      }
    },
  })),
);
